import { useState, useEffect, useCallback } from 'react';
import { BREAKPOINTS, Breakpoint } from '../utils/themeConfig';

export interface ResponsiveState {
  width: number;
  height: number;
  currentBreakpoint: Breakpoint;
  isXs: boolean;
  isSm: boolean;
  isMd: boolean;
  isLg: boolean;
  isXl: boolean;
  isXxl: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

const RESIZE_DEBOUNCE_MS = 150;
// Server-side render fallback: a desktop-sized viewport.
const FALLBACK_VIEWPORT_WIDTH = 1200;
const FALLBACK_VIEWPORT_HEIGHT = 800;

const getCurrentBreakpoint = (width: number): Breakpoint => {
  if (width >= BREAKPOINTS.xxl) {
    return 'xxl';
  }
  if (width >= BREAKPOINTS.xl) {
    return 'xl';
  }
  if (width >= BREAKPOINTS.lg) {
    return 'lg';
  }
  if (width >= BREAKPOINTS.md) {
    return 'md';
  }
  if (width >= BREAKPOINTS.sm) {
    return 'sm';
  }
  return 'xs';
};

const createResponsiveState = (width: number, height: number): ResponsiveState => {
  const currentBreakpoint = getCurrentBreakpoint(width);
  
  return {
    width,
    height,
    currentBreakpoint,
    isXs: currentBreakpoint === 'xs',
    isSm: currentBreakpoint === 'sm',
    isMd: currentBreakpoint === 'md',
    isLg: currentBreakpoint === 'lg',
    isXl: currentBreakpoint === 'xl',
    isXxl: currentBreakpoint === 'xxl',
    isMobile: width < BREAKPOINTS.md,
    isTablet: width >= BREAKPOINTS.md && width < BREAKPOINTS.lg,
    isDesktop: width >= BREAKPOINTS.lg,
  };
};

const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
};

export const useResponsiveDesign = (): ResponsiveState => {
  const [dimensions, setDimensions] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : FALLBACK_VIEWPORT_WIDTH,
    height: typeof window !== 'undefined' ? window.innerHeight : FALLBACK_VIEWPORT_HEIGHT,
  }));

  const debouncedDimensions = useDebounce(dimensions, RESIZE_DEBOUNCE_MS);

  const handleResize = useCallback(() => {
    setDimensions({ width: window.innerWidth, height: window.innerHeight });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('orientationchange', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [handleResize]);

  return createResponsiveState(debouncedDimensions.width, debouncedDimensions.height);
};
